/**
 * The routes the consumer bar menu goes to.
 *
 * By definition these are not part of the flow: they are what a signed-in consumer keeps about
 * themselves - their details, their appointments, their orders - and not a step on the way to a
 * booking. The flow config does list some of them (accountDetails1 ships as a subroute of the
 * account step), but that is how they are reachable, not what they are, and FlowContext refuses
 * them a step of their own.
 *
 * What follows from that, and what the chrome does with it:
 *
 * - no navigation dots: there is no step to point at;
 * - no shopping cart in the app bar: the consumer is not in a booking here;
 * - no title from the flow: a route without a step has no label, so the view sets its own title
 *   through `setTitle` on the flow context;
 * - the back arrow does not walk the flow but returns to it, to the step the menu was opened
 *   from, or to the start of the widget when that step is gone (`returnToFlow`).
 */
export const CONSUMER_ROUTES = [
    'accountDetails1',
    'appointments',
    'orders',
    'subscriptions',
    'waitinglists'
];
/**
 * The views the appointments list opens, and the confirmations they end on.
 *
 * Outside the flow for the same reason the menu routes are, and they set their own title in the
 * same way. They differ in one thing only: their back arrow goes to the list they were opened
 * from rather than into the flow.
 */
export const APPOINTMENT_ROUTES = [
    'cancel',
    'cancelled',
    'reschedule',
    'rescheduled'
];
/**
 * The route name in the current hash, without any parameters.
 *
 * '#/oa_widget_' is 12 characters, so what is left is the route the widget is on; a
 * parameterized route ('detail/12/name') is cut back to its base.
 */
export const currentRouteName = () => location.hash.substring(12).split('/')[0];
/** Whether a route belongs to the consumer's own screens, and so sits outside the flow. */
export const isConsumerRoute = (route) => CONSUMER_ROUTES.includes(route)
    || APPOINTMENT_ROUTES.includes(route);
/**
 * The route the back arrow returns to, for a consumer route that has one of its own.
 *
 * Only the appointment views do: they hang off the appointments list. Everything else returns
 * into the flow, which is `returnToFlow` on the flow context rather than a route.
 */
export const parentConsumerRoute = (route) => APPOINTMENT_ROUTES.includes(route) ? 'appointments' : undefined;
